import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { INotification, INotificationPayload } from '@app/modules/notifications/interface/notification';
import {
  NOTIFICATION_PROPS_DICTIONARY,
  NOTIFICATION_SECTION,
  NOTIFICATION_TYPE,
} from '@app/modules/notifications/conts/notification';
import { GENDER } from '@app/shared/consts/gender';
import { SEX } from '@app/shared/consts/sex';
import { INSTRUCTION_FOLLOWING } from '@app/shared/consts/instruction-following';
import { ETHNICITY } from '@app/shared/consts/ethnicity';
import { DISABILITIES } from '@app/shared/consts/disabilities';
import { RELIGION } from '@app/shared/consts/religion';
import { MENTAL_HEALTH_PREFERENCES } from '@app/shared/consts/mental-health-preferences';
import { BOOL } from '@app/shared/consts/bool';
import { ADDITIONAL_HEALTH_PREFERENCES } from '@app/shared/consts/additional-health-preferences';
import { DatePipe } from '@angular/common';
import { CRITICAL_INFO_TYPE } from '@app/modules/critical-info/consts/type';
import { INVITATION_STATUS } from '@app/modules/invitations/consts/status';
import { USER_ROLE } from '@app/modules/user/consts/role';

@Component({
  selector: 'app-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationItemComponent {
  @Output() accentEvent = new EventEmitter<INotification>();

  @Output() declineEvent = new EventEmitter<INotification>();

  @Input() userRole: USER_ROLE = null;

  /** "Personal information updated (3 changes)" — one line, not twelve. */
  public formUpdateHeadline(payload: INotificationPayload): string {
    const count = payload.changeCount || (payload.changes || []).length;
    const section = payload.section ? String(payload.section) : 'This form';
    return count === 1 ? `${section} updated` : `${section} updated — ${count} changes`;
  }

  /** Field name in words; falls back to the raw key rather than showing blank. */
  public fieldLabel(propertyName: string): string {
    return NOTIFICATION_PROPS_DICTIONARY[propertyName] || propertyName;
  }

  @Input() public set item(value: INotification) {
    const { type, payload } = value;

    if (type === NOTIFICATION_TYPE.CriticalInfoUpdated) {
      const { changedTo, changedFrom, propertyName } = payload;

      // Same convention as the treatment-choice cards: the answer is WORDS,
      // and color reinforces it — green = yes, red = no, amber = everything
      // in between. `null` is a real value now ("let my agent decide",
      // "not sure") and must NEVER render as the no label.
      const definitions: Record<
        string,
        { name: string; yes: string; no: string }
      > = {
        [CRITICAL_INFO_TYPE.resuscitation]: {
          name: 'Resuscitation (CPR)',
          yes: 'Yes — attempt CPR',
          no: 'No — do not resuscitate (DNR)',
        },
        [CRITICAL_INFO_TYPE.intubation]: {
          name: 'Intubation',
          yes: 'Yes — use a breathing tube',
          no: 'No — do not intubate (DNI)',
        },
        [CRITICAL_INFO_TYPE.organDonation]: {
          name: 'Organ donation',
          yes: 'Yes — organ donor',
          no: 'No — not a donor',
        },
        [CRITICAL_INFO_TYPE.geneticTesting]: {
          name: 'Genetic testing',
          yes: 'Yes — testing allowed',
          no: 'No — no genetic testing',
        },
      };
      const definition = definitions[propertyName];
      const labelOf = (v: unknown): string =>
        v === true
          ? definition?.yes ?? 'Yes'
          : v === false
          ? definition?.no ?? 'No'
          : 'Not decided — ask me or my health care agent';
      const polarityOf = (v: unknown): string =>
        v === true ? 'yes' : v === false ? 'no' : 'other';

      value = {
        ...value,
        payload: {
          ...payload,
          changedToLabel: labelOf(changedTo),
          changedFromLabel: labelOf(changedFrom),
          changedToPolarity: polarityOf(changedTo),
          changedFromPolarity: polarityOf(changedFrom),
          propertyNameLabel: definition?.name ?? propertyName,
        },
      };
    }

    if (
      type === NOTIFICATION_TYPE.GeneralInfoUpdated ||
      type === NOTIFICATION_TYPE.LivingWillUpdated
    ) {
      const { section, changedTo, changedFrom, propertyName } = payload;
      let sectionLabel = "Patient's";

      switch (section) {
        case NOTIFICATION_SECTION.Notarization:
          sectionLabel = "Notary Public's";
          break;
        case NOTIFICATION_SECTION.ProxyInfo:
          sectionLabel = "Primary Proxy's ";
          break;
        case NOTIFICATION_SECTION.FirstWitnessInfo:
          sectionLabel = "Witness 1's";
          break;
        case NOTIFICATION_SECTION.SecondWitnessInfo:
          sectionLabel = "Witness 2's";
          break;
      }

      switch (propertyName) {
        case 'dateOfBirth':
        case 'graduationDate':
        case 'entryStartDate':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo:
                changedTo === null
                  ? changedTo
                  : this.datePipe.transform(changedTo, 'MM-dd-yyyy'),
              changedFrom:
                changedFrom === null
                  ? changedFrom
                  : this.datePipe.transform(changedFrom, 'MM-dd-yyyy'),
            },
          };
          break;
        case 'gender':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo:
                changedTo === null ? changedTo : GENDER[`${changedTo}`],
              changedFrom:
                changedFrom === null ? changedFrom : GENDER[`${changedFrom}`],
            },
          };
          break;
        case 'sex':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo: changedTo === null ? changedTo : SEX[`${changedTo}`],
              changedFrom:
                changedFrom === null ? changedFrom : SEX[`${changedFrom}`],
            },
          };
          break;
        case 'instructionsFollowing':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo:
                changedTo === null
                  ? changedTo
                  : INSTRUCTION_FOLLOWING[changedTo],
              changedFrom:
                changedFrom === null
                  ? changedFrom
                  : INSTRUCTION_FOLLOWING[changedFrom],
            },
          };
          break;
        case 'ethnicity':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo:
                changedTo === null || changedTo.length === 0
                  ? null
                  : changedTo.map((i) => ETHNICITY[i]).join(', '),
              changedFrom:
                changedFrom === null || changedFrom.length === 0
                  ? null
                  : changedFrom.map((i) => ETHNICITY[`${i}`]).join(', '),
            },
          };
          break;
        case 'disabilities':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo:
                changedTo === null || changedTo.length === 0
                  ? null
                  : changedTo.map((i) => DISABILITIES[i]).join(', '),
              changedFrom:
                changedFrom === null || changedFrom.length === 0
                  ? null
                  : changedFrom.map((i) => DISABILITIES[`${i}`]).join(', '),
            },
          };
          break;
        case 'religion':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo: changedTo === null ? changedTo : RELIGION[changedTo],
              changedFrom:
                changedFrom === null ? changedFrom : RELIGION[changedFrom],
            },
          };
          break;
        case 'mentalHealthPreferences':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo: this.formatHealthPreferences(
                changedTo,
                MENTAL_HEALTH_PREFERENCES
              ),
              changedFrom: this.formatHealthPreferences(
                changedFrom,
                MENTAL_HEALTH_PREFERENCES
              ),
            },
          };
          break;
        case 'additionalMentalHealthPreferences':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo: this.formatHealthPreferences(
                changedTo,
                ADDITIONAL_HEALTH_PREFERENCES
              ),
              changedFrom: this.formatHealthPreferences(
                changedFrom,
                ADDITIONAL_HEALTH_PREFERENCES
              ),
            },
          };
          break;
        case 'isNextOfKin':
          value = {
            ...value,
            payload: {
              ...payload,
              changedTo: changedTo ? 'Yes' : 'No',
              changedFrom: changedFrom ? 'Yes' : 'No',
            },
          };
          break;
      }

      value = {
        ...value,
        payload: {
          ...value.payload,
          propertyNameLabel: `${sectionLabel} ${NOTIFICATION_PROPS_DICTIONARY[propertyName]}`,
        },
      };
    }

    this._item = value;
  }

  public get item(): INotification {
    return this._item;
  }

  public NOTIFICATION_TYPE = NOTIFICATION_TYPE;
  public INVITATION_STATUS = INVITATION_STATUS;
  public USER_ROLE = USER_ROLE;

  private _item: INotification;

  /**
   * @param datePipe
   */
  constructor(private readonly datePipe: DatePipe) {}

  private formatHealthPreferences(target: string[], dictionary): string {
    let result = null;

    if (target !== null && target.length > 0) {
      const [a, b, c] = target;

      if (b) {
        result = b
          .split(',')
          .sort((d, e) => +d - +e)
          .map((i) => dictionary[i])
          .join(', ');
      }

      if (c) {
        result += `(${c})`;
      }

      if (!result && a) {
        result = BOOL[a];
      }
    }

    return result;
  }
}
